import {
  consentState,
  hasMarketing,
  hasStatistics,
  subscribeToConsent,
} from "@/tracking/consent/consentStore";
import { themeState } from "@/shared/stores/themeStore";

/**
 * The campaign a lead came from, held until there is permission to keep it.
 *
 * <p>Click identifiers arrive in the landing URL and are gone by the time the visitor asks for a
 * quote — they configure a door for twenty minutes first, and the router has rewritten the address
 * several times. So they are read once, on arrival, and held.</p>
 *
 * <p>Held in a module variable, not in storage. A gclid is a marketing identifier: writing it to
 * sessionStorage before anyone has agreed to marketing would be the processing the banner is asking
 * about, done before the answer. It moves to sessionStorage the moment marketing is granted — that
 * is what makes it survive a reload — and is dropped outright if the answer is no.</p>
 *
 * <p>The GA4 client id travels with them but is not one of them. It is read straight off the <c>_ga</c>
 * cookie at the moment the lead is posted, and it answers to statistics rather than to marketing — so
 * it is gated separately, and a visitor who granted only one of the two categories gets exactly the
 * half they granted. No holding is needed: <c>_ga</c> only exists because GA4 is running, and GA4 only
 * runs after statistics was granted, so the cookie's presence is itself the evidence.</p>
 */

const STORAGE_KEY = "lobas.attribution";

export interface Attribution {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
  fbclid?: string;
  ttclid?: string;
}

/** Only what a platform can actually attribute with. Anything else is not worth keeping. */
const PARAMS: ReadonlyArray<[keyof Attribution, string]> = [
  ["utmSource", "utm_source"],
  ["utmMedium", "utm_medium"],
  ["utmCampaign", "utm_campaign"],
  ["utmContent", "utm_content"],
  ["utmTerm", "utm_term"],
  ["gclid", "gclid"],
  ["gbraid", "gbraid"],
  ["wbraid", "wbraid"],
  ["fbclid", "fbclid"],
  ["ttclid", "ttclid"],
];

/** A campaign parameter is short and opaque; anything long is not one, and is not carried. */
const MAX_LENGTH = 512;

/** GA4's own first-party cookie. Written by gtag, never by us. */
const GA_COOKIE = "_ga";

/** The client id proper: two decimal halves, a random and a first-seen timestamp. */
const GA_CLIENT_ID = /^\d{1,20}\.\d{1,20}$/;

/** The version tag "_ga" opens with. Its absence means some other cookie took the name. */
const GA_VERSION = /^GA\d+$/;

/**
 * The GA4 client id out of a raw cookie string, or null when there is not one to read.
 *
 * <p><c>_ga</c> holds "GA1.1.1234567890.1234567890": a version tag, a domain-depth tag, then the
 * client id — which is itself dotted, and is therefore the last TWO segments rather than the last
 * one. Splitting on the final dot is the mistake this function exists to not make.</p>
 *
 * <p>Pure, and takes the cookie string rather than reading <c>document.cookie</c> itself, so the
 * parsing can be pinned without a DOM. Every unexpected shape answers null: the client id is worth
 * having, but a guess at one stitches this order onto a stranger's session in the analytics
 * property, which is worse than reporting the order without one.</p>
 */
export function readGaClientId(cookie: string): string | null {
  if (typeof cookie !== "string" || cookie.length === 0) return null;

  const prefix = `${GA_COOKIE}=`;

  for (const part of cookie.split(";")) {
    const entry = part.trim();
    // An exact name match. The per-property "_ga_G-XXXXXXX" cookies share the prefix and hold a
    // session id, not a client id, and startsWith on "_ga" alone would happily return one.
    if (!entry.startsWith(prefix)) continue;

    const value = entry.slice(prefix.length).trim();
    const segments = value.split(".");
    // The first matching cookie wins and no later one is considered: a duplicate name shadows rather
    // than supplements, and reading past it would pick a value the browser itself does not send.
    if (segments.length < 4 || !GA_VERSION.test(segments[0])) return null;

    const clientId = segments.slice(-2).join(".");
    return GA_CLIENT_ID.test(clientId) ? clientId : null;
  }

  return null;
}

/** The client id this browser currently carries. Total: the cookie getter throws in a sandboxed frame. */
function currentGaClientId(): string | null {
  try {
    return readGaClientId(document.cookie);
  } catch {
    return null;
  }
}

let held: Attribution | null = null;
let subscribed = false;

function fromUrl(search: string): Attribution | null {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    return null;
  }

  const found: Attribution = {};
  let any = false;

  for (const [key, param] of PARAMS) {
    const raw = params.get(param);
    if (raw === null) continue;
    const value = raw.trim();
    if (value.length === 0 || value.length > MAX_LENGTH) continue;
    found[key] = value;
    any = true;
  }

  return any ? found : null;
}

function readStored(): Attribution | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw === null ? null : (JSON.parse(raw) as Attribution);
  } catch {
    return null;
  }
}

function persist(value: Attribution): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage refused — private mode, or a quota. The in-memory copy still serves this page view.
  }
}

export function forgetAttribution(): void {
  held = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do; there was nothing readable to remove.
  }
}

/**
 * Read the landing URL, once. Call from bootstrap, before the router has a chance to rewrite it.
 *
 * <p>First touch wins: a visitor who returns through a second ad inside the same session is still
 * the lead the first click produced, and overwriting would hand the conversion to the wrong campaign.</p>
 */
export function captureAttribution(search: string = window.location.search): void {
  if (held === null) held = readStored();
  if (held !== null) return;

  const found = fromUrl(search);
  if (found === null) return;

  held = found;
  if (hasMarketing()) persist(found);

  if (!subscribed) {
    subscribed = true;
    // Granting marketing later is what makes the held copy durable; refusing is what deletes it.
    subscribeToConsent((snapshot) => {
      if (snapshot.categories.marketing) {
        if (held !== null) persist(held);
      } else {
        forgetAttribution();
      }
    });
  }
}

export function currentAttribution(): Attribution | null {
  if (held === null) held = readStored();
  return held;
}

/**
 * Send what is known about where a lead came from, so a sale months later can still be traced back.
 *
 * <p>Always against a receipt id — the server refuses a capture it cannot tie to a recorded decision,
 * which is what makes the stored row defensible rather than merely present.</p>
 *
 * <p><b>Two independent gates, and neither may stand in for the other.</b> The click identifiers report
 * a sale to an advertising platform and need marketing; the GA4 client id sews the sale onto the visit
 * that produced it inside the analytics property, and needs statistics. Combining them into one check
 * is how a visitor who accepted analytics and declined advertising ends up either counted in neither
 * or counted in both. So a row may legitimately carry only click identifiers, or only a client id.</p>
 *
 * <p>The server repeats both checks against the receipt it holds. That is not redundancy worth removing:
 * this side decides what to offer, and the receipt on file decides what may be kept.</p>
 */
export function postLeadAttribution(leadId: string): boolean {
  const receiptId = consentState.record?.consentReceiptId;
  if (!receiptId) return false;

  const campaign = hasMarketing() ? currentAttribution() : null;
  const gaClientId = hasStatistics() ? currentGaClientId() : null;

  // Neither half survived its gate, so there is nothing here worth a row — a lead id and a timestamp
  // is data kept for no purpose, and the server declines it in the same terms.
  if (campaign === null && gaClientId === null) return false;

  const body = JSON.stringify({
    leadId,
    consentReceiptId: receiptId,
    ...(campaign ?? {}),
    ...(gaClientId === null ? {} : { gaClientId }),
  });

  try {
    void fetch("/api/tracking/lead-attribution", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      credentials: "omit",
    }).catch(() => {
      // The lead itself is already saved. Losing its campaign costs attribution, not the lead.
    });
    return true;
  } catch {
    return false;
  }
}

/** The tenant the receipt was recorded under, for callers that need it alongside. */
export function attributionTenant(): string {
  return themeState.tenant.id;
}
